import type { ReactNode } from 'react';
import type { Group } from '__types__/v1/models/Group';
import type { GroupOption } from 'v5/core/entity-schema/entitySchema.types';
import type { MultiSelectRenderValue } from 'v5/patterns/forms/schema-form/fields/OptionRefMultiSelect';
import { ResourceGroupBadge } from 'v5/patterns/resource/ResourceGroupBadge';
import { ColorDot } from 'v5/patterns/widgets/ColorDot';

/** Only active groups of the current resource type are available for selection. */
export function getResourceGroupOptions(
  groups: Group[],
  resourceTypeId: number | undefined
): GroupOption[] {
  return groups
    .filter(group => !group.archived && group.entity_id === resourceTypeId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id)
    .map(group => ({
      value: String(group.id),
      label: group.name || `Group ${group.id}`,
      colour: group.colour ?? null
    }));
}

/** Dropdown row of a group: its colour dot and name. */
export function renderResourceGroupOption(option: GroupOption): ReactNode {
  return (
    <>
      <ColorDot color={option.colour} aria-hidden />
      {option.label}
    </>
  );
}

/** Resolve assigned badges separately, including archived groups and resource snapshots. */
export function createResourceGroupValueRenderer(
  groups: Group[],
  selectedGroups: Group[] = []
): MultiSelectRenderValue {
  const groupsById = new Map(
    [...selectedGroups, ...groups]
      .filter(group => typeof group.id === 'number')
      .map(group => [String(group.id), group])
  );

  return ({ value, readOnly, removeButton }) => {
    const group = groupsById.get(value);
    if (!group) return undefined;

    return (
      <ResourceGroupBadge
        label={group.name || `Group ${group.id}`}
        colour={group.colour}
        archived={group.archived}
        readOnly={readOnly}
      >
        {removeButton}
      </ResourceGroupBadge>
    );
  };
}
